import { isApiError } from '@pa/api'
import { CloseButton, copyText, Drawer, fmtDateTimeOf, timeAgo, useToast } from '@pa/ui'
import { CircleAlert, Copy } from 'lucide-react'
import { useId, type ReactNode } from 'react'
import type { ErrorsAdapter } from '../hooks/adapters'
import { slotLabel } from '../model/format'
import { CODE_HINTS } from '../model/steps'
import { StepPill } from './ErrorTable'

export interface ErrorDrawerProps {
  adapter: ErrorsAdapter
  errorId: string
  timeZone: string
  /** S-08: the clinic, linked to its S-04 Errors tab. */
  renderClinicLink?: ((clinic: { id: string; name: string }) => ReactNode) | undefined
  onClose: () => void
}

/**
 * C-03a (UserFlows C-03a, mockup `ErrorDrawer`): the header (code, time, step), the full message, the context
 * (Source, Visit type, Location, Provider, Slot, HTTP status, Request ID), and the raw event: `JSON.stringify` of the
 * `ErrorEvent` (API.md §3.5) with Copy → toast "Copied". Close only: no resolve, acknowledge or delete. S-08 adds the
 * clinic with a link to S-04 (UserFlows S-08).
 */
export function ErrorDrawer({ adapter, errorId, timeZone, renderClinicLink, onClose }: ErrorDrawerProps) {
  const titleId = useId()
  const toast = useToast()
  const q = adapter.useOne(errorId)
  const e = q.data
  const missing = q.isError && isApiError(q.error) && q.error.code === 'not_found'
  const raw = e ? JSON.stringify(e, null, 2) : ''
  const c = e?.context
  const hint = e ? CODE_HINTS[e.code] : undefined

  return (
    <Drawer onClose={onClose} labelledBy={titleId}>
      <div className="drawer-head">
        <span
          className="kpi-icon"
          style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--red-50)', color: 'var(--red)' }}
          aria-hidden
        >
          <CircleAlert />
        </span>
        <div style={{ minWidth: 0 }}>
          <h2 id={titleId} style={{ fontSize: 17 }}>
            {e ? (
              <code className="err-code lg">{e.code}</code>
            ) : missing ? (
              'This error no longer exists'
            ) : q.isError ? (
              'Couldn’t load this error'
            ) : (
              'Loading…'
            )}
          </h2>
          {e && (
            <>
              <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                {fmtDateTimeOf(e.occurredAt, timeZone)} · {timeAgo(e.occurredAt)}
              </div>
              <div style={{ marginTop: 8 }}>
                <StepPill step={e.step} />
              </div>
            </>
          )}
        </div>
        <CloseButton onClick={onClose} />
      </div>
      <div className="drawer-body">
        {e && c ? (
          <>
            <p className="err-message">{e.message}</p>
            {hint && <p className="err-hint">{hint}</p>}
            <h3 className="drawer-h">Context</h3>
            <dl className="kv">
              {e.clinic && (
                <>
                  <dt>Clinic</dt>
                  <dd>{renderClinicLink ? renderClinicLink(e.clinic) : e.clinic.name}</dd>
                </>
              )}
              <dt>Source</dt>
              <dd>{e.source ?? 'Unknown'}</dd>
              {c.visitType && (
                <>
                  <dt>Visit type</dt>
                  <dd>{c.visitType.name}</dd>
                </>
              )}
              {c.location && (
                <>
                  <dt>Location</dt>
                  <dd>{c.location.name}</dd>
                </>
              )}
              {c.provider && (
                <>
                  <dt>Provider</dt>
                  <dd>{c.provider.name}</dd>
                </>
              )}
              {c.slotStart && (
                <>
                  <dt>Slot</dt>
                  <dd>{slotLabel(c.slotStart)}</dd>
                </>
              )}
              {e.httpStatus !== null && (
                <>
                  <dt>HTTP status</dt>
                  <dd>{e.httpStatus}</dd>
                </>
              )}
              <dt>Request ID</dt>
              <dd className="mono">{e.requestId}</dd>
            </dl>
            <div className="row" style={{ justifyContent: 'space-between', margin: '24px 0 10px' }}>
              <h3 className="drawer-h" style={{ margin: 0 }}>
                Raw event
              </h3>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  void copyText(raw).then((ok) => {
                    toast(ok ? 'Copied' : "Couldn't copy. Select the text and copy it.", ok ? 'success' : 'error')
                  })
                }}
              >
                <Copy aria-hidden /> Copy
              </button>
            </div>
            <pre className="code-block" data-testid="raw-event">
              {raw}
            </pre>
          </>
        ) : q.isError && !missing ? (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              void q.refetch()
            }}
          >
            Retry
          </button>
        ) : q.isPending ? (
          <span className="spinner" aria-hidden />
        ) : null}
      </div>
    </Drawer>
  )
}
